/** @jsxImportSource @emotion/react */
import { Button, Input, message, Space, Table, Switch, Image } from "antd";
import lodash from "lodash";
import React from "react";
import { useParams } from "react-router-dom";
import {
  LayoutContent,
  LayoutContentFilterBar,
  SEO,
  TypographySmall,
} from "../../../components";
import { FilterItem } from "../../../components/LayoutContentFilterBar";
import {
  ModalAddHome,
  ModalRequestHome,
  ModalEditHome,
  ModalChangeStatusHome,
  ModalDeleteRequestHome,
} from "../../../components";
import { GetHomeDto } from "../../../services/Home/dto/get.dto";
import {
  HomeUsecase,
  useHomeDispatch,
  useHomeState,
} from "../../../usecases/Home";
import { HomeColumns } from "../../../usecases/Home/usecase.d";
import { useTenantDispatch, useTenantState } from "../../../usecases/Tenant";
import { TenantUsecase } from "../../../usecases/Tenant/usecase";
import {
  FilterParams,
  ModalHomeFilter,
} from "../../../components/Home/ModalHomeFilter";
import { ModalHomeSettingColumns } from "../../../components";
import * as Styles from "./styles";
import { SettingUsecase, useSettingDispatch } from "../../../usecases/Setting";
import { RouteInfos } from "../../../common/routeInfo";

interface TenantPathParams {
  id: string;
}

const rangeLabel: { [index: string]: string } = {
  range1: "Range 1",
  range2: "Range 2",
};

const storeyLabel: { [index: string]: string } = {
  single: "Single",
  double: "Double",
};

const HomesComponent: React.FC = () => {
  const tenantDispatch = useTenantDispatch();
  const tenantState = useTenantState();
  const homeState = useHomeState();
  const homeDispatch = useHomeDispatch();
  const settingDispatch = useSettingDispatch();

  const [openNewHomeModal, setOpenNewHomeModal] = React.useState(false);
  const [openRequestNewHomeModal, setOpenRequestNewHomeModal] =
    React.useState(false);
  const [openEditHomeModal, setOpenEditHomeModal] = React.useState(false);
  const [openChangeStatusHomeModal, setOpenChangeStatusHomeModal] =
    React.useState(false);
  const [openDeleteRequestHomeModal, setOpenDeleteRequestHomeModal] =
    React.useState(false);
  const [openFilterModal, setOpenFilterModal] = React.useState(false);
  const [openSettingModal, setOpenSettingModal] = React.useState(false);
  const [displayColumns, setDisplayColumns] = React.useState<HomeColumns>(
    {} as HomeColumns,
  );
  const [selectedHome, setSelectedHome] = React.useState<
    GetHomeDto | undefined
  >();

  const { id: tenantIdParams } = useParams<TenantPathParams>();
  const tenantId = React.useMemo(() => {
    if (tenantIdParams) {
      return tenantIdParams;
    }
    return tenantState.detail?.id;
  }, [tenantIdParams, tenantState.detail?.id]);
  const [filter, setFilter] = React.useState<FilterParams>({
    tenant_id: tenantId,
  });

  const filterArrayMemo = React.useMemo(() => {
    const filterArray: FilterItem[] = [];
    const filterObject = filter as Record<string, string>;
    for (const key in filter) {
      if (key === "keyword" || key === "tenant_id" || !filterObject[key]) {
        continue;
      }
      let label = key.replace(/([A-Z])/g, " $1");
      label = label.charAt(0).toUpperCase() + label.slice(1);

      filterArray.push({
        label,
        key,
        value: filterObject[key],
      });
    }
    return filterArray;
  }, [filter]);

  const onItemStatusChangeHandle = React.useCallback(
    (value) => async (checked: boolean) => {
      if (homeDispatch) {
        const homeUsecase = new HomeUsecase(homeDispatch);
        await homeUsecase.updateStatus({
          id: value.id,
          isActive: checked,
        });
      }
    },
    [homeDispatch],
  );

  const onItemEditClickHandle = React.useCallback(
    (value) => () => {
      setSelectedHome(value);
      setOpenEditHomeModal(true);
    },
    [],
  );

  const onModalAddHomeCancelHandle = React.useCallback(() => {
    setOpenNewHomeModal(false);
  }, []);

  const onModalAddHomeSuccessHandle = React.useCallback(() => {
    setOpenNewHomeModal(false);
    message.success("Created new home successful");
  }, []);

  // const onButtonRequestNewClickHandle = React.useCallback(() => {
  //   setOpenRequestNewHomeModal(true);
  // }, []);

  const onModalRequestHomeCancelHandle = React.useCallback(() => {
    setOpenRequestNewHomeModal(false);
  }, []);

  const onModalRequestHomeSuccessHandle = React.useCallback(() => {
    setOpenRequestNewHomeModal(false);
    message.success("Requested new home successful");
  }, []);

  const onModalEditHomeCancelHandle = React.useCallback(() => {
    setOpenEditHomeModal(false);
    setSelectedHome(undefined);
  }, []);

  const onModalEditHomeSuccessHandle = React.useCallback(() => {
    setOpenEditHomeModal(false);
    setSelectedHome(undefined);
    message.success("Updated home successful");
  }, []);

  const onModalChangeStatusHomeCancelHandle = React.useCallback(() => {
    setOpenChangeStatusHomeModal(false);
    setSelectedHome(undefined);
  }, []);

  const onModalChangeStatusHomeSuccessHandle = React.useCallback(() => {
    setOpenChangeStatusHomeModal(false);
    setSelectedHome(undefined);
    message.success("Change status home successful");
  }, []);

  const onFilterClickHandle = React.useCallback(() => {
    setOpenFilterModal(true);
  }, []);

  const onFilterItemCloseHandle = React.useCallback(
    (item: FilterItem) => {
      const filterClone = {
        ...filter,
      } as Record<string, string | undefined>;
      for (const key in filter) {
        if (key === item.key) {
          delete filterClone[key];
          break;
        }
      }
      console.info("filter before", filter);
      console.info("filter after", filterClone);
      setFilter(filterClone as FilterParams);
    },
    [filter],
  );

  const onModalHomeFilterCancelHandle = React.useCallback(() => {
    setOpenFilterModal(false);
  }, []);

  const onModalHomeFilterSearchHandle = React.useCallback(
    (newFilter: FilterParams) => {
      setFilter(newFilter);
      setOpenFilterModal(false);
    },
    [],
  );

  const onSettingClickHandle = React.useCallback(() => {
    setOpenSettingModal(true);
  }, []);

  const onModalHomeSettingColumnsCancelHandle = React.useCallback(() => {
    setOpenSettingModal(false);
  }, []);

  const onModalHomeSettingColumnsSubmitHandle = React.useCallback(
    (columns: HomeColumns) => {
      if (homeDispatch) {
        const homeUsecase = new HomeUsecase(homeDispatch);
        homeUsecase.setDisplayColumns(columns);
        setDisplayColumns(columns);
        setOpenSettingModal(false);
      }
    },
    [homeDispatch],
  );

  const onModalDeleteRequestHomeCancelHandle = React.useCallback(() => {
    setSelectedHome(undefined);
    setOpenDeleteRequestHomeModal(false);
  }, []);

  const onModalDeleteRequestHomeSuccessHandle = React.useCallback(() => {
    setSelectedHome(undefined);
    setOpenDeleteRequestHomeModal(false);
    message.success("Deleted Request Home successful");
  }, []);

  const onButtonDeleteClickHandle = React.useCallback(
    (item) => () => {
      setSelectedHome(item);
      setOpenDeleteRequestHomeModal(true);
    },
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChangeHandle = React.useCallback(
    lodash.debounce((event) => {
      setFilter({
        ...filter,
        keyword: event.target.value,
      });
    }, 300),
    [],
  );

  React.useEffect(() => {
    async function asyncLoadDetail() {
      if (tenantDispatch && tenantId) {
        const tenantUsecase = new TenantUsecase(tenantDispatch);
        const tenantItem = await tenantUsecase.get({ id: tenantId });

        if (settingDispatch) {
          const settingUsecase = new SettingUsecase(settingDispatch);
          settingUsecase.setMenuDetail({
            id: tenantItem.id,
            title: tenantItem.tenantName,
            routeInfo: RouteInfos.HomeBuilderHomes,
          });
        }
      }
    }
    asyncLoadDetail();
  }, [filter, settingDispatch, tenantDispatch, tenantId]);

  React.useEffect(() => {
    async function asyncLoadData() {
      if (homeDispatch) {
        const homeUsecase = new HomeUsecase(homeDispatch);
        await homeUsecase.getList(filter);

        const columns = homeUsecase.getDisplayColumns();
        setDisplayColumns(columns);
      }
    }

    asyncLoadData();
  }, [filter, homeDispatch]);

  return (
    <LayoutContent
      title={
        tenantIdParams
          ? `HOME BUILDERS / ${
              tenantState.detail
                ? tenantState.detail.tenantName.toUpperCase()
                : ""
            } / HOMES`
          : "HOMES"
      }
      toolbar={
        <Space size="large">
          <Input.Search
            placeholder="Keyword"
            allowClear
            style={{ width: 300, verticalAlign: "middle" }}
            size="large"
            onChange={onKeywordChangeHandle}
          />
          {/* Hide this feature because it's unclear the logical */}
          {/* <Button
            type="primary"
            size="large"
            onClick={onButtonRequestNewClickHandle}
          >
            <img src={Images.ADD} css={Styles.iconStyle} />
            <span>Request new</span>
          </Button> */}
        </Space>
      }
    >
      <SEO title="Homes" />
      <ModalAddHome
        visible={openNewHomeModal}
        onCancel={onModalAddHomeCancelHandle}
        onSuccess={onModalAddHomeSuccessHandle}
      />
      <ModalRequestHome
        visible={openRequestNewHomeModal}
        onCancel={onModalRequestHomeCancelHandle}
        onSuccess={onModalRequestHomeSuccessHandle}
      />
      {selectedHome && (
        <ModalEditHome
          visible={openEditHomeModal}
          onCancel={onModalEditHomeCancelHandle}
          onSuccess={onModalEditHomeSuccessHandle}
          item={selectedHome}
        />
      )}
      {selectedHome && (
        <ModalChangeStatusHome
          visible={openChangeStatusHomeModal}
          onCancel={onModalChangeStatusHomeCancelHandle}
          onSuccess={onModalChangeStatusHomeSuccessHandle}
          item={selectedHome}
        />
      )}
      {selectedHome && openDeleteRequestHomeModal && (
        <ModalDeleteRequestHome
          visible={openDeleteRequestHomeModal}
          onCancel={onModalDeleteRequestHomeCancelHandle}
          onSuccess={onModalDeleteRequestHomeSuccessHandle}
          item={selectedHome}
        />
      )}
      {openFilterModal && (
        <ModalHomeFilter
          visible={openFilterModal}
          onCancel={onModalHomeFilterCancelHandle}
          onSearch={onModalHomeFilterSearchHandle}
          filter={filter}
        />
      )}
      {openSettingModal && (
        <ModalHomeSettingColumns
          visible={openSettingModal}
          onCancel={onModalHomeSettingColumnsCancelHandle}
          onSubmit={onModalHomeSettingColumnsSubmitHandle}
          columns={displayColumns}
        />
      )}
      <LayoutContentFilterBar
        onFilterClick={onFilterClickHandle}
        onFilterClose={onFilterItemCloseHandle}
        onSettingClick={onSettingClickHandle}
        filters={filterArrayMemo}
      />
      <Table
        dataSource={homeState.data}
        css={Styles.tableStyle}
        pagination={false}
      >
        {displayColumns.homeFloorplanMapUrl && (
          <Table.Column
            title="Floorplan Map"
            render={(value) =>
              value.homeFloorplanMapUrl ? (
                <Image width={200} src={value.homeFloorplanMapUrl} />
              ) : (
                <></>
              )
            }
            align="center"
          />
        )}
        {displayColumns.homeName && (
          <Table.Column
            title="Name"
            dataIndex="homeName"
            key="homeName"
            align="center"
          />
        )}
        {displayColumns.homeRange && (
          <Table.Column
            title="Range"
            render={(value) => (
              <TypographySmall>{rangeLabel[value.homeRange]}</TypographySmall>
            )}
            align="center"
          />
        )}
        {displayColumns.homeBedNo && (
          <Table.Column
            title="Number of Bed"
            dataIndex="homeBedNo"
            key="homeBedNo"
            align="center"
          />
        )}
        {displayColumns.homeBathNo && (
          <Table.Column
            title="Number of Bath"
            dataIndex="homeBathNo"
            key="homeBathNo"
            align="center"
          />
        )}
        {displayColumns.homeGarageNo && (
          <Table.Column
            title="Number of Garage"
            dataIndex="homeGarageNo"
            key="homeGarageNo"
            align="center"
          />
        )}
        {displayColumns.homeFromPrice && (
          <Table.Column
            title="From Price"
            dataIndex="homeFromPrice"
            key="homeFromPrice"
            align="center"
          />
        )}
        {displayColumns.homeToPrice && (
          <Table.Column
            title="To Price"
            dataIndex="homeToPrice"
            key="homeToPrice"
            align="center"
          />
        )}
        {displayColumns.homeLotSize && (
          <Table.Column
            title="Lot Size"
            dataIndex="homeLotSize"
            key="homeLotSize"
            align="center"
          />
        )}
        {displayColumns.homeStorey && (
          <Table.Column
            title="Storey"
            render={(value) => (
              <TypographySmall>{storeyLabel[value.homeStorey]}</TypographySmall>
            )}
            align="center"
          />
        )}
        <Table.Column
          title="Status"
          render={(value) =>
            value.status === "Done" ? (
              <Switch
                onChange={onItemStatusChangeHandle(value)}
                checked={value.isActive}
              />
            ) : (
              value.status
            )
          }
        />
        <Table.Column
          render={(value) => {
            if (value.status === "Done") {
              return (
                <Button type="link" onClick={onItemEditClickHandle(value)}>
                  <TypographySmall>Edit</TypographySmall>
                </Button>
              );
            } else if (value.status === "Request") {
              return (
                <Button
                  type="link"
                  danger
                  onClick={onButtonDeleteClickHandle(value)}
                >
                  <TypographySmall>Delete</TypographySmall>
                </Button>
              );
            } else {
              return <></>;
            }
          }}
        />
      </Table>
    </LayoutContent>
  );
};

export const Homes = React.memo(HomesComponent);
